import StickyPrompt from 'components/Common/StickyPromptCard/StickyPrompt'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import useClickOutside from 'hooks/useClickOutside'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { matchPath } from 'react-router'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import { checkPromoDisplayConfig, fetchTargetedPromoDetails, removePromoDisplay } from 'actions/MarketingAction'
import TextButton from 'LuxKit/Button/TextButton'
import { getCookies, setCookie } from 'lib/web/cookieUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import * as Analytics from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import AccountAccessPromptIcon from 'components/Account/AccountAccessPrompt/AccountAccessPromptIcon'
import ValentineTakeover from 'constants/siteTakeover/valentineTakeover'
import Image from 'components/Common/Image'

const PROMO_DISPLAY_COOKIE_NAME = 'promoDisplay'
// To ensure we delay the promo display by at least 1 second
const PROMO_DISPLAY_DELAY_MOFIDIER = 1000
const UNKNOWN_PROMO_CODE = 'UNKNOWN_PROMO_CODE'

const AccessWrapper = styled.div`
  position: relative;
`

const CloseButton = styled(IconButton)`
  position: absolute;
  right: ${rem(20)};
  top: ${rem(20)};
`

const AccessPrompt = styled(StickyPrompt)`
  padding: ${rem(24)};

  ${mediaQueryUp.tablet} {
    width: ${rem(400)};
  }
`

const PromoCodeContainer = styled.div`
  padding: ${rem(8)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.six};
  width: 100%;
  height: min-content;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const getOfferId = (state: App.State): string | undefined => {
  if (!state.routeHistory.currentPathParams) return
  let offerId = (state.routeHistory.currentPathParams as any).offerId
  // For some dum reason the cruises analytics page params has id and not offerId
  if (!offerId) offerId = (state.routeHistory.currentPathParams as any).id
  return offerId
}

const getPromoDisplayConfig = (pathname: string, promoDisplayConfig: App.PromoDisplayConfigMap | undefined, region: string, overridePromoDisplayConfig: App.PromoDisplayConfig | undefined) => {
  if (overridePromoDisplayConfig) {
    return overridePromoDisplayConfig
  }

  if (!promoDisplayConfig) {
    return
  }
  return Object.values(promoDisplayConfig)?.find((config) => !!matchPath(pathname, { path: `/${region}${config.urlPath}`, exact: true }))
}

interface Props {
  overridePromoDisplayPrompt?: App.PromoDisplayConfig
}

function PromoDisplayPrompt({ overridePromoDisplayPrompt }: Props) {
  const promptRef = useRef<HTMLDivElement>(null)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const domainUserId = useAppSelector(state => state.auth.domainUserId)
  const currentRegion = useAppSelector(state => state.geo.currentRegionCode)
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const storedPromoDisplayConfig = useAppSelector(state => state.marketing.promoDisplayConfigs)
  const [promoDisplayConfig, setPromoDisplayConfig] = useState<App.PromoDisplayConfig | undefined>(getPromoDisplayConfig(currentPath, storedPromoDisplayConfig, currentRegion, overridePromoDisplayPrompt))
  const [show, setShow] = useState<boolean>(false)
  const [, copy] = useCopyToClipboard()
  const [isCopied, setIsCopied] = useState(false)
  const dispatch = useAppDispatch()
  const offerId = useAppSelector(state => getOfferId(state))
  const passAuthStateCheck = useMemo(() => {
    if ((promoDisplayConfig?.useTargetedPromo) && !isLoggedIn) {
      return false
    }
    if (promoDisplayConfig?.authState === 'logged_in_and_out') {
      return true
    }
    if (promoDisplayConfig?.authState === 'logged_in' && isLoggedIn) {
      return true
    }
    if (promoDisplayConfig?.authState === 'logged_out' && !isLoggedIn) {
      return true
    }
    return false
  }, [promoDisplayConfig, isLoggedIn])

  const HeaderIcon = useMemo(() => {
    if (promoDisplayConfig?.codeName === 'LOVEISINTHEAIR' && ValentineTakeover.regions.has(currentRegion)) {
      return <Image width={90} id="ravf7iouyaoeqloomvue" enhancedFormats={false} />
    } else {
      return <AccountAccessPromptIcon />
    } }, [promoDisplayConfig, currentRegion])

  const setPromoDisplayCookie = useCallback((promoDisplayId: string) => {
    const allCookies = getCookies()
    const promoDisplayCookie = allCookies[PROMO_DISPLAY_COOKIE_NAME]
    setCookie(PROMO_DISPLAY_COOKIE_NAME, `${promoDisplayCookie ? promoDisplayCookie + ',' : ''}${promoDisplayId}`, 30 * 24 * 60 * 60, '/', 'Lax')
  }, [])

  useEffect(() => {
    const newPromoDisplayConfig = getPromoDisplayConfig(currentPath, storedPromoDisplayConfig, currentRegion, overridePromoDisplayPrompt)
    // If the promo needs an offer check and the offer check fails, we should not show the promo
    const offerCheckFailed = newPromoDisplayConfig?.needsOfferCheck && (!offerId || (currentPath in newPromoDisplayConfig.passOfferCheck && !newPromoDisplayConfig.passOfferCheck[currentPath]))
    const shouldDisablePromoDisplay = (!isLoggedIn && (newPromoDisplayConfig?.useTargetedPromo || newPromoDisplayConfig?.needsOfferCheck)) || offerCheckFailed
    if (shouldDisablePromoDisplay) {
      setPromoDisplayConfig(undefined)
    } else {
      setPromoDisplayConfig(newPromoDisplayConfig)

      // Check if we need to perform additional offer validation
      if (newPromoDisplayConfig?.needsOfferCheck && offerId && !(currentPath in (newPromoDisplayConfig.passOfferCheck ?? {}))) {
        dispatch(checkPromoDisplayConfig(newPromoDisplayConfig.promoDisplayId, offerId, currentPath, domainUserId))
      }
    }
  }, [currentPath, currentRegion, storedPromoDisplayConfig, isLoggedIn, overridePromoDisplayPrompt, offerId, dispatch, domainUserId])

  // Handle display logic and timing
  useEffect(() => {
    if (!promoDisplayConfig) {
      return
    }
    if (promoDisplayConfig.isTargetedPromo) {
      if (isLoggedIn) {
        dispatch(fetchTargetedPromoDetails(promoDisplayConfig.promoDisplayId))
      }
      return
    }
    let shouldShow = !!promoDisplayConfig
    if (shouldShow && promoDisplayConfig.needsOfferCheck) {
      shouldShow = !!promoDisplayConfig.passOfferCheck?.[currentPath]
    }
    const timer = setTimeout(() => setShow(shouldShow), promoDisplayConfig.delayDuration * PROMO_DISPLAY_DELAY_MOFIDIER)
    return () => clearTimeout(timer)
  }, [promoDisplayConfig, dispatch, isLoggedIn, setShow, currentPath])

  // Handle copy state timeout
  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isCopied])

  // Track analytics when promo is shown
  useEffect(() => {
    if (show) {
      Analytics.trackEvent(promotionViewEvent({
        promotionContext: { promotionName: 'promo_display', promotionType: 'prompt' },
        promotionCodeContext: { codeName: promoDisplayConfig?.codeName ?? UNKNOWN_PROMO_CODE },
      }))
    }
  }, [show, promoDisplayConfig])

  const onClickCopyIcon = useCallback(() => {
    copy(promoDisplayConfig?.codeName ?? '')
    setIsCopied(true)
    Analytics.trackEvent(promotionClickEvent({
      promotionContext: { promotionName: 'promo_display', promotionType: 'prompt' },
      promotionCodeContext: { codeName: promoDisplayConfig?.codeName ?? UNKNOWN_PROMO_CODE },
    }))
  }, [promoDisplayConfig, copy])

  const dismissPrompt = useCallback(() => {
    setShow(false)
  }, [])

  useClickOutside(promptRef, dismissPrompt, show)
  const onClose = useCallback(() => {
    if (promoDisplayConfig?.displayMode === 'appear_once') {
      setPromoDisplayCookie(promoDisplayConfig.promoDisplayId)
      sessionStorage.setItem('promoDisplayId', promoDisplayConfig.promoDisplayId)
      dispatch(removePromoDisplay(promoDisplayConfig.promoDisplayId))
      setPromoDisplayConfig(undefined)
    }
    dismissPrompt()
  }, [promoDisplayConfig, dismissPrompt, dispatch, setPromoDisplayCookie])

  if (!passAuthStateCheck) {
    return null
  }

  return <AccessWrapper>
    <AccessPrompt show={show} sticky={false} ref={promptRef}>
      <CloseButton
        kind="tertiary"
        shape="square"
        onClick={onClose}
      >
        <LineTimesIcon />
      </CloseButton>
      <Group direction="vertical" gap={16} fullWidth>
        <Group direction="vertical" gap={16} horizontalAlign="center">
          {HeaderIcon}
          <Heading variant="heading3">{promoDisplayConfig?.headerText}</Heading>
        </Group>
        <BodyTextBlock variant="medium">{promoDisplayConfig?.bodyText}</BodyTextBlock>
        {promoDisplayConfig?.expiresAt && <BodyTextBlock variant="medium">This promotion code will expire on {(new Date(promoDisplayConfig.expiresAt)).toString()}</BodyTextBlock>}
        <PromoCodeContainer>
          <BodyTextBlock variant="medium" weight="bold">
            {promoDisplayConfig?.codeName}
          </BodyTextBlock>
          <IconButton
            kind="tertiary"
            size="small"
            shape="circle"
            onClick={onClickCopyIcon}
          >
            {isCopied ? <LineCheckIcon /> : <LineCopyIcon />}
          </IconButton>
        </PromoCodeContainer>
        <TextButton kind="primary" onClick={onClickCopyIcon}>Copy this code</TextButton>
        {promoDisplayConfig?.displayMode === 'appear_once' && <TextButton kind="secondary" onClick={onClose}>I do not want to see this again.</TextButton>}
      </Group>
    </AccessPrompt>
  </AccessWrapper>
}

export default React.memo(PromoDisplayPrompt)
